if (!customElements.get('mb-predictive-search')) {
  class MartenbrownPredictiveSearch extends HTMLElement {
    constructor() {
      super();
      this.requestVersion = 0;
      this.contentTerm = '';
      this.options = [];
      this.selectedIndex = -1;
    }

    connectedCallback() {
      if (this.events) return;
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-mb-predictive-results]');
      this.status = this.querySelector('[data-mb-search-status]');
      if (!this.input?.id || !this.input.form || !this.results || !this.dataset.url) return;

      this.events = new AbortController();
      const listener = { signal: this.events.signal };
      this.input.setAttribute('role', 'combobox');
      this.input.setAttribute('aria-autocomplete', 'list');
      this.input.setAttribute('aria-haspopup', 'listbox');
      this.input.setAttribute('aria-expanded', 'false');
      this.input.setAttribute('autocomplete', 'off');

      this.input.addEventListener('input', (event) => {
        if (!this.composing && !event.isComposing) this.scheduleSearch();
      }, listener);
      this.input.addEventListener('compositionstart', () => {
        this.composing = true;
        this.dismiss();
      }, listener);
      this.input.addEventListener('compositionend', () => {
        this.composing = false;
        this.scheduleSearch();
      }, listener);
      this.input.addEventListener('focus', () => {
        if (!this.ignoreFocus) this.showOrSearch();
      }, listener);
      this.input.form.addEventListener('reset', () => this.dismiss(), listener);
      this.input.form.addEventListener('submit', () => this.dismiss(), listener);
      this.results.addEventListener('mousedown', (event) => {
        // Safari can blur to body before a tapped link receives its click,
        // hiding the panel. Keep input focus until native link activation;
        // leave touch/pointer events untouched so scrolling still works.
        if (event.button === 0 && document.activeElement === this.input && event.target.closest('a[href]')) {
          event.preventDefault();
        }
      }, listener);
      this.addEventListener('keydown', (event) => this.onKeydown(event), listener);
      this.addEventListener('focusout', (event) => {
        if (event.relatedTarget) {
          if (!this.contains(event.relatedTarget)) this.dismiss();
        } else {
          queueMicrotask(() => {
            if (!this.contains(document.activeElement)) this.dismiss();
          });
        }
      }, listener);
      document.addEventListener('pointerdown', (event) => {
        if (!this.contains(event.target)) this.dismiss();
      }, listener);

      const resize = () => {
        if (!this.results.hidden) this.updateHeight();
      };
      window.addEventListener('resize', resize, listener);
      window.addEventListener('scroll', resize, { ...listener, capture: true, passive: true });
      window.visualViewport?.addEventListener('resize', resize, listener);
      window.visualViewport?.addEventListener('scroll', resize, listener);
    }

    disconnectedCallback() {
      this.dismiss();
      this.events?.abort();
      this.events = null;
    }

    getQuery() {
      return this.input.value.trim();
    }

    cancelPending() {
      this.requestVersion += 1;
      clearTimeout(this.timer);
      this.timer = null;
      this.request?.abort();
      this.request = null;
      this.removeAttribute('loading');
      this.input?.removeAttribute('aria-busy');
    }

    hideResults() {
      if (this.results) this.results.hidden = true;
      this.removeAttribute('open');
      this.input?.setAttribute('aria-expanded', 'false');
      this.selectOption(-1);
    }

    dismiss() {
      this.cancelPending();
      this.hideResults();
      this.setStatus('');
    }

    scheduleSearch() {
      this.dismiss();
      const query = this.getQuery();
      if (query.length < 2) return;
      const version = this.requestVersion;
      this.timer = setTimeout(() => {
        this.timer = null;
        this.fetchResults(query, version);
      }, 200);
    }

    showOrSearch() {
      const query = this.getQuery();
      if (query.length < 2 || this.composing) return;
      if (query === this.contentTerm) {
        this.openResults();
      } else if (!this.timer && !this.request) {
        this.scheduleSearch();
      }
    }

    isCurrent(query, version) {
      return this.isConnected && version === this.requestVersion &&
        query === this.getQuery() && this.contains(document.activeElement);
    }

    async fetchResults(query, version) {
      if (!this.isCurrent(query, version)) return;
      this.request = new AbortController();
      this.setAttribute('loading', '');
      this.input.setAttribute('aria-busy', 'true');
      this.setStatus(this.dataset.loadingText || '');

      try {
        const url = new URL(this.dataset.url, window.location.origin);
        url.searchParams.set('q', query);
        url.searchParams.set('resources[type]', 'product');
        url.searchParams.set('resources[limit]', '8');
        url.searchParams.set('resources[options][unavailable_products]', 'show');
        url.searchParams.set('section_id', 'mb-predictive-search');
        const response = await fetch(url.toString(), { signal: this.request.signal });
        if (!response.ok) throw new Error('Predictive search unavailable');
        const html = await response.text();
        if (!this.isCurrent(query, version)) return;

        const content = new DOMParser().parseFromString(html, 'text/html')
          .querySelector('[data-mb-predictive-content]');
        const listbox = content?.querySelector('[data-mb-search-options]');
        if (!listbox) throw new Error('Missing predictive search results');

        listbox.id = `${this.input.id}-listbox`;
        this.results.replaceChildren(content);
        this.options = Array.from(listbox.querySelectorAll('[data-mb-search-option]'));
        this.options.forEach((option, index) => {
          option.id = `${this.input.id}-option-${index + 1}`;
          option.setAttribute('aria-selected', 'false');
        });
        this.input.setAttribute('aria-controls', listbox.id);
        this.contentTerm = query;
        this.openResults();
      } catch (error) {
        if (!this.isCurrent(query, version) || error.name === 'AbortError') return;
        this.hideResults();
        this.setStatus(this.dataset.errorText || '');
      } finally {
        if (version === this.requestVersion) {
          this.request = null;
          this.removeAttribute('loading');
          this.input.removeAttribute('aria-busy');
        }
      }
    }

    openResults() {
      this.results.hidden = false;
      this.setAttribute('open', '');
      this.input.setAttribute('aria-expanded', 'true');
      this.selectOption(-1);
      this.updateHeight();
      this.setStatus(this.results.querySelector('[data-mb-predictive-content]')?.dataset.mbResultStatus || '');
    }

    updateHeight() {
      const viewport = window.visualViewport;
      const bottom = viewport ? viewport.height + viewport.offsetTop : window.innerHeight;
      const height = Math.max(0, Math.floor(bottom - this.results.getBoundingClientRect().top - 12));
      this.style.setProperty('--mb-search-available-height', `${height}px`);
    }

    setStatus(message) {
      if (this.status) this.status.textContent = message;
    }

    selectOption(index) {
      this.options.forEach((option, optionIndex) => {
        option.setAttribute('aria-selected', String(optionIndex === index));
      });
      this.selectedIndex = index;
      if (index < 0) {
        this.input?.removeAttribute('aria-activedescendant');
        return;
      }
      const selected = this.options[index];
      this.input.setAttribute('aria-activedescendant', selected.id);
      // Scroll the dropdown only; scrollIntoView can move the whole page.
      const optionRect = selected.getBoundingClientRect();
      const panelRect = this.results.getBoundingClientRect();
      if (optionRect.bottom > panelRect.bottom) this.results.scrollTop += optionRect.bottom - panelRect.bottom;
      if (optionRect.top < panelRect.top) this.results.scrollTop -= panelRect.top - optionRect.top;
    }

    onKeydown(event) {
      if (this.composing || event.isComposing || event.keyCode === 229) return;
      if (event.key === 'Escape') {
        if (this.results.hidden && !this.timer && !this.request) return;
        event.preventDefault();
        this.dismiss();
        if (event.target !== this.input) {
          this.ignoreFocus = true;
          this.input.focus({ preventScroll: true });
          this.ignoreFocus = false;
        }
        return;
      }
      if (event.target !== this.input || event.altKey || event.ctrlKey || event.metaKey) return;
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        if (this.getQuery().length < 2) return;
        event.preventDefault();
        if (this.results.hidden) this.showOrSearch();
        if (this.results.hidden || !this.options.length) return;
        const direction = event.key === 'ArrowDown' ? 1 : -1;
        const index = this.selectedIndex < 0 ? (direction === 1 ? 0 : this.options.length - 1) :
          (this.selectedIndex + direction + this.options.length) % this.options.length;
        this.selectOption(index);
      } else if (event.key === 'Enter' && !this.results.hidden && this.selectedIndex >= 0) {
        const link = this.options[this.selectedIndex]?.querySelector('a');
        if (!link) return;
        event.preventDefault();
        link.click();
        this.dismiss();
      }
    }
  }

  customElements.define('mb-predictive-search', MartenbrownPredictiveSearch);
}
